import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSuppliers, useInvalidate } from '../hooks/useApi';
import api from '../services/api';
import toast from 'react-hot-toast';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';

export default function Suppliers() {
  const [search, setSearch] = useState('');
  const { data: suppliers, isLoading } = useSuppliers(search);
  const { invalidateSuppliers } = useInvalidate();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', name: '', phone: '', email: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/suppliers/${editing.id}`, form);
        toast.success('U përditësua!');
      } else {
        await api.post('/suppliers', form);
        toast.success('U shtua!');
      }
      setShowModal(false);
      setEditing(null);
      setForm({ code: '', name: '', phone: '', email: '' });
      invalidateSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    }
  };

  const handleEdit = (supplier) => {
    setEditing(supplier);
    setForm({ code: supplier.code, name: supplier.name, phone: supplier.phone || '', email: supplier.email || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Jeni i sigurt?')) return;
    try {
      await api.delete(`/suppliers/${id}`);
      toast.success('U fshi!');
      invalidateSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Gabim');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Furnitorët</h1>
        <button onClick={() => { setEditing(null); setForm({ code: '', name: '', phone: '', email: '' }); setShowModal(true); }} className="btn-primary flex items-center gap-2">
          <PlusIcon className="w-5 h-5" /> Shto Furnitor
        </button>
      </div>

      <div className="card">
        <div className="relative mb-4">
          <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" placeholder="Kërko furnitor..." value={search} onChange={(e) => setSearch(e.target.value)} className="input-field pl-10" />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Kodi</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Emri</th>
                  <th className="text-left py-3 px-2 font-medium text-gray-500">Tel</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Detyrim</th>
                  <th className="text-right py-3 px-2 font-medium text-gray-500">Veprime</th>
                </tr>
              </thead>
              <tbody>
                {suppliers?.map((supplier) => (
                  <tr key={supplier.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-2 font-medium">{supplier.code}</td>
                    <td className="py-3 px-2">
                      <Link to={`/suppliers/${supplier.id}`} className="text-primary-600 hover:underline">{supplier.name}</Link>
                    </td>
                    <td className="py-3 px-2 text-gray-500">{supplier.phone}</td>
                    <td className={`py-3 px-2 text-right font-medium ${supplier.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      €{supplier.balance?.toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-right">
                      <button onClick={() => handleEdit(supplier)} className="p-1 text-gray-500 hover:text-primary-600"><PencilIcon className="w-5 h-5" /></button>
                      <button onClick={() => handleDelete(supplier.id)} className="p-1 text-gray-500 hover:text-red-600 ml-1"><TrashIcon className="w-5 h-5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold mb-4">{editing ? 'Ndrysho' : 'Shto'} Furnitor</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Kodi</label><input type="text" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="input-field" required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Emri</label><input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" required /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label><input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-field" /></div>
              <div><label className="block text-sm font-medium text-gray-700 mb-1">Email</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-field" /></div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Anulo</button>
                <button type="submit" className="btn-primary flex-1">Ruaj</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
